import { AtIcon, CircleInfoIcon, LinkIcon, PhoneIcon, PinIcon } from "@xola/icons";
import { Button, ComboBox, Label, Modal, Phone, SubmitButton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton, SwitchField } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { PartnerObject, PurchaseRuleLink, XolaPartner, XolaPartnerConnectionType, XolaPartnerStatus } from "types";
import { boolean, object, string } from "yup";
import { api } from "@/api/api";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getDefaultLogoUrl } from "@/models/Seller";

interface EditPartnerConnectionModalProps {
    isOpen: boolean;
    partner: XolaPartner;
    onClose: () => void;
}

export const EditPartnerConnectionModal = ({ isOpen, partner, onClose }: EditPartnerConnectionModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent partner={partner} onClose={onClose} />
        </Modal>
    );
};

// TODO: Refactor this into a common modal component so that both edit and create uses same
const ModalContent = ({ partner, onClose }: Except<EditPartnerConnectionModalProps, "isOpen">) => {
    const isInitiator = partner.initiator?.id === partner.seller.id;
    const { partnerSeller } = partner;
    const invalidate = useInvalidate();

    const address = [partnerSeller.streetAddress ?? "", partnerSeller.city ?? "", partnerSeller.state ?? ""].join(", ");
    const { aboutBusiness, phone, email } = partnerSeller?.preferences?.network ?? {};
    const { website } = partnerSeller;

    const { data: termsTemplates } = useAutoFetchCollection<PurchaseRuleLink>(["purchaseRuleLinks"], {
        "tags._id": "Affiliate",
    });

    const { mutateAsync: updatePartner, isLoading: isUpdatingPartner } = useMutation(
        async (payload: { status: string }) => {
            return api.patch(`/partners/${partner.id}`, payload);
        },
    );

    const { mutateAsync: createPartner, isLoading: isCreatingPartner } = useMutation(async (payload: XolaPartner) => {
        return api.post(`/partners`, payload);
    });

    const isLoading = isUpdatingPartner || isCreatingPartner;
    const handleEditPartner = async (payload: { status: string }) => {
        await updatePartner(payload);
        invalidate("partners", partner.id);
        onClose();
    };

    const handleCreateConnection = async (formValues: PartnerConnectionFormValues) => {
        const { seller, partnerSeller, sellerResells, partnerResells } = formValues;
        const connectionType = getConnectionTypeFromFormValues(sellerResells, partnerResells);

        const payload: any = {
            object: PartnerObject.XOLA_PARTNER,
            status: XolaPartnerStatus.PENDING,
            seller,
            partnerSeller,
            connectionType,
        };

        if (
            connectionType === XolaPartnerConnectionType.RESELLER ||
            connectionType === XolaPartnerConnectionType.SUPPLIER_RESELLER
        ) {
            payload.purchaseRuleLink = { id: formValues.purchaseRuleLink };
        }

        await createPartner(payload as XolaPartner);
        invalidate("partners");
        onClose();
    };

    const handleArchiveConnection = async () => {
        await handleEditPartner({
            status: XolaPartnerStatus.ARCHIVED,
        });
    };

    const handleSubmit = async (values: PartnerConnectionFormValues) => {
        // If there is a change in partner connection, create a new connection
        await (partner.connectionType === getConnectionTypeFromFormValues(values.sellerResells, values.partnerResells)
            ? handleAcceptConnection(values)
            : handleCreateConnection(values));
    };

    const handleAcceptConnection = async (values: PartnerConnectionFormValues) => {
        const data = {
            status: XolaPartnerStatus.ACCEPTED,
            ...(values.purchaseRuleLink && { purchaseRuleLink: { id: values.purchaseRuleLink } }),
        };
        await handleEditPartner(data);
    };

    const handleDeclineConnection = async () => {
        await handleEditPartner({
            status: XolaPartnerStatus.DECLINED,
        });
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validationSchema={getValidationSchema()}
            initialValues={getInitialValues(partner)}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue, handleSubmit, errors }) => (
                <>
                    <Modal.Header>{partnerSeller.name}</Modal.Header>
                    <Modal.Body>
                        <div className="space-y-6">
                            <div className="space-y-4">
                                <p className="text-lg font-bold">Basic Information</p>
                                <div className="flex space-x-2">
                                    <img
                                        alt={`Logo for ${partner.partnerSeller.name}`}
                                        src={getDefaultLogoUrl(partnerSeller, "small")}
                                        className="no-translate h-15 w-15 rounded"
                                    />
                                    <div>
                                        <NoTranslate as="p" className="text-md font-bold">
                                            {partner.partnerSeller.name}
                                        </NoTranslate>
                                        {website ? (
                                            <p className="space-x-1">
                                                <LinkIcon />
                                                <NoTranslate>{website}</NoTranslate>
                                            </p>
                                        ) : null}
                                        {address ? (
                                            <p className="space-x-1">
                                                <PinIcon />
                                                <NoTranslate>{address}</NoTranslate>
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <div className="">
                                <p className="text-lg font-bold">About the Business</p>
                                <p>{aboutBusiness}</p>
                            </div>
                            <div className="">
                                <p className="text-lg font-bold">Contact Details</p>
                                <NoTranslate as="p">
                                    <PhoneIcon className="mr-1" />
                                    <Phone>{phone}</Phone>
                                </NoTranslate>
                                <NoTranslate as="p">
                                    <AtIcon className="mr-1" />
                                    {email}
                                </NoTranslate>
                            </div>
                            <div className="space-y-4">
                                <p className="text-lg font-bold">Partnership Settings requested</p>
                                <SwitchField
                                    isChecked={values.sellerResells}
                                    isDisabled={isInitiator}
                                    onChange={(value) => setFieldValue("sellerResells", value)}
                                >
                                    {isInitiator ? (
                                        <span>I want to sell their products</span>
                                    ) : (
                                        <span>They request you to sell their products</span>
                                    )}
                                </SwitchField>
                                <div>
                                    <SwitchField
                                        isChecked={values.partnerResells}
                                        isDisabled={isInitiator}
                                        onChange={(value) => {
                                            setFieldValue("partnerResells", value);
                                            if (value) {
                                                setFieldValue("purchaseRuleLink", termsTemplates?.[0]?.id ?? null);
                                            } else {
                                                setFieldValue("purchaseRuleLink", undefined);
                                            }
                                        }}
                                    >
                                        {isInitiator ? (
                                            <span>I want them to sell my products</span>
                                        ) : (
                                            <span>They want to sell your products</span>
                                        )}
                                    </SwitchField>
                                </div>
                                {values.partnerResells ? (
                                    <div className="ml-10">
                                        <Label className={clsx(errors?.purchaseRuleLink && "!text-danger")}>
                                            Distribution Terms {errors.purchaseRuleLink}
                                            <Tooltip content="These are your discount and commission terms that apply to this operator when they distribute your products.">
                                                <CircleInfoIcon className="ml-1" />
                                            </Tooltip>
                                        </Label>
                                        <ComboBox
                                            placeholder="Select Terms template"
                                            appendTo={document.body}
                                            value={termsTemplates.find(
                                                (template) => template.id === values.purchaseRuleLink,
                                            )}
                                            menuPortalTarget={document.body}
                                            options={termsTemplates}
                                            getOptionValue={(option: PurchaseRuleLink) => option.id}
                                            getOptionLabel={(option: PurchaseRuleLink) => option.name}
                                            classNames={{
                                                option: () => "no-translate",
                                                singleValue: () => "no-translate",
                                                menuList: () => (termsTemplates?.length > 4 ? "h-40" : ""),
                                            }}
                                            styles={{
                                                menuPortal: (base: any) => ({
                                                    ...base,
                                                    zIndex: 30,
                                                    display: "block",
                                                }),
                                            }}
                                            onChange={(template: PurchaseRuleLink) =>
                                                setFieldValue("purchaseRuleLink", template.id)
                                            }
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        {partner.status === XolaPartnerStatus.PENDING && !isInitiator ? (
                            <div className="flex justify-between">
                                <Button color="danger" className="align-left" onClick={handleDeclineConnection}>
                                    Decline
                                </Button>
                                <div className="space-x-2">
                                    <SecondaryButton onClick={onClose}>Decide Later</SecondaryButton>
                                    {partner.connectionType ===
                                    getConnectionTypeFromFormValues(values.sellerResells, values.partnerResells) ? (
                                        <SubmitButton
                                            isLoading={isLoading}
                                            color="success"
                                            disabled={false}
                                            type="submit"
                                            onClick={handleSubmit}
                                        >
                                            Connect
                                        </SubmitButton>
                                    ) : (
                                        <SubmitButton
                                            isLoading={isLoading}
                                            disabled={false}
                                            type="submit"
                                            onClick={handleSubmit}
                                        >
                                            Suggest Changes
                                        </SubmitButton>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-between">
                                {isInitiator && partner.status === XolaPartnerStatus.PENDING ? (
                                    <InlineConfirmDeletePopover
                                        confirmButtonText="Confirm Delete Request"
                                        appendTo={document.body}
                                        onConfirm={handleArchiveConnection}
                                    >
                                        <Button color="danger" className="align-left">
                                            Delete Request
                                        </Button>
                                    </InlineConfirmDeletePopover>
                                ) : null}
                                <SecondaryButton onClick={onClose}>Close</SecondaryButton>
                            </div>
                        )}
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

export interface PartnerConnectionFormValues {
    seller: { id: ID };
    partnerSeller: { id: ID };
    sellerResells: boolean;
    partnerResells: boolean;
    status: string;
    purchaseRuleLink?: ID;
}

const getInitialValues = (partner: XolaPartner, termsTemplate?: PurchaseRuleLink): PartnerConnectionFormValues => {
    const { connectionType } = partner;
    const sellerResells = [XolaPartnerConnectionType.SUPPLIER, XolaPartnerConnectionType.SUPPLIER_RESELLER].includes(
        connectionType,
    );
    const partnerResells = [XolaPartnerConnectionType.RESELLER, XolaPartnerConnectionType.SUPPLIER_RESELLER].includes(
        connectionType,
    );

    const initialValues: PartnerConnectionFormValues = {
        seller: { id: partner.seller.id },
        partnerSeller: { id: partner.partnerSeller.id },
        sellerResells,
        partnerResells,
        status: partner.status,
    };

    if (partner.purchaseRuleLink?.id) {
        initialValues.purchaseRuleLink = partner.purchaseRuleLink.id;
    } else if (partnerResells && !partner.purchaseRuleLink?.id && termsTemplate) {
        initialValues.purchaseRuleLink = termsTemplate.id;
    }

    return initialValues;
};

const getValidationSchema = () => {
    return object({
        sellerResells: boolean().required(),
        partnerResells: boolean().required(),
        purchaseRuleLink: string().when("partnerResells", {
            is: true,
            then: string().required("is required"),
            otherwise: string().notRequired(),
        }),
    });
};

const getConnectionTypeFromFormValues = (sellerResells: boolean, partnerResells: boolean) => {
    if (sellerResells && partnerResells) {
        return XolaPartnerConnectionType.SUPPLIER_RESELLER;
    }

    if (sellerResells) {
        return XolaPartnerConnectionType.SUPPLIER;
    }

    return XolaPartnerConnectionType.RESELLER;
};
